'use client'

import {
  AnimatePresence,
  domAnimation,
  LazyMotion,
  m as motion,
  MotionProps,
  motionValue,
  useTransform,
} from 'framer-motion'

import { FC, useEffect, useRef } from 'react'

import { IChildren, IClassName } from '@/app/shared/interfaces/common.interface'

// interface
interface IMoveIcon extends IChildren, IClassName, Omit<MotionProps, 'children'> {
  rotate?: number
  valueMoving?: number
}

// component
const MoveIconComponent: FC<Readonly<IMoveIcon>> = ({
  children,
  className,
  rotate,
  valueMoving,
  ...props
}) => {
  const x = motionValue(0)
  const y = motionValue(0)
  const value = valueMoving ?? 30
  const container = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!container.current) return

    const handleMouseMove = (event: MouseEvent) => {
      const { clientX, clientY } = event

      if (!event.view) return

      const xOffset = (clientX - event.view.innerWidth / 2) / value
      const yOffset = (clientY - event.view.innerHeight / 2) / value

      x.set(xOffset)
      y.set(yOffset)
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [x, y, value])

  const translateX = useTransform(x, [-50, 50], [-25, 25])
  const translateY = useTransform(y, [-50, 50], [-25, 25])

  return (
    <LazyMotion features={domAnimation}>
      <AnimatePresence>
        <motion.div
          {...props}
          className={className}
          style={{
            x: translateX,
            y: translateY,
            rotate: rotate ?? 0,
          }}
          ref={container}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </LazyMotion>
  )
}

export default MoveIconComponent
